import { Link } from "react-router-dom";
import { Section, Overline, PrimaryCTA, SecondaryCTA, TrustStrip } from "@/components/Section";
import { PricingTable } from "@/pages/solutions/DomainPage";
import StatusChip from "@/components/StatusChip";
import ChoroplethMotif from "@/components/ChoroplethMotif";
import { CheckCircle2, ArrowRight, ArrowUpRight, Activity, Calendar, FileCheck2, RefreshCw } from "lucide-react";

const STEPS = [
  {
    n: "01",
    t: "Scoping & kickoff",
    d: "We confirm the legislative driver (federal HNA template, BC HNR, or municipal mandate), the community boundary, and the engagement signatory.",
  },
  {
    n: "02",
    t: "Evidence assembly",
    d: "Public data layers (T3010, StatsCan, provincial registries) are loaded into your tenant. Your data layer is integrated alongside, never blended.",
  },
  {
    n: "03",
    t: "Indicator review",
    d: "Analyst-operated review of indicator panels against the regulated method version. You see every number's lineage before it lands in a draft.",
  },
  {
    n: "04",
    t: "Draft report + walkthrough",
    d: "A versioned compliant report is generated. The walkthrough is with the human professional who will sign it — not a workflow notification.",
  },
  {
    n: "05",
    t: "Sign-off & live monitoring",
    d: "Once adopted, the same evidence base powers monitoring dashboards. Refresh cycles produce new versioned outputs, with year-over-year comparability built in.",
  },
];

const PRODUCTS = [
  { status: "LIVE", t: "HNA Engine", d: "Compliant HNA from federal HICC or BC HNR, versioned, sign-off included." },
  { status: "LIVE", t: "RoCA Benchmark", d: "Regional cost/outcome benchmarking report, one-off or annual refresh." },
  { status: "LIVE", t: "Community Housing Snapshot", d: "A lightweight housing profile for small municipalities and nonprofits." },
  { status: "LIVE", t: "Council Pack", d: "Add-on council-ready export bundle for existing tenants." },
  { status: "PLANNED", t: "Homelessness System Intelligence", d: "Counts, chronic homelessness, shelter capacity, by-name lists." },
  { status: "PLANNED", t: "Non-Market Housing Inventory", d: "Non-market unit counts against the measured deficit, by community." },
  { status: "PLANNED", t: "Housing Target Monitor", d: "Sub-target schedules and statutory reporting for BC Target Orders." },
  { status: "PLANNED", t: "Grant Evidence Pack — Housing", d: "One-off evidence bundle sized for a single funding application." },
];

const PRICING_ROWS = [
  { product: "HNA Engine", who: "Municipalities, planning directors", model: "Setup + annual monitoring", price: "$18k–35k + $6k–10k/yr" },
  { product: "RoCA Benchmark", who: "Municipalities, housing coalitions", model: "One-off / refresh", price: "$3.5k–5k; $2k/yr refresh" },
  { product: "Community Housing Snapshot", who: "Small municipalities, nonprofits", model: "One-off", price: "$1,500; $500 refresh" },
  { product: "Council Pack", who: "Existing tenants", model: "Add-on", price: "$1k/yr or $400 one-off" },
  { product: "Homelessness System Intelligence", status: "PLANNED", who: "Reaching Home entities, coordinators", model: "Subscription", price: "$9k–15k/yr + $5k setup" },
  { product: "Non-Market Housing Inventory", status: "PLANNED", who: "Municipalities, housing authorities", model: "Subscription", price: "$4k–6k/yr per community" },
  { product: "Housing Target Monitor", status: "PLANNED", who: "BC municipalities under Target Orders", model: "Subscription", price: "$7,500/yr" },
  { product: "Grant Evidence Pack — Housing", status: "PLANNED", who: "Small housing & shelter nonprofits", model: "One-off / unlimited", price: "$600–1.2k; $2.5k/yr" },
];

const ALWAYS_INCLUDED = [
  "Versioned methodology access",
  "Canadian-resident data hosting",
  "Named professional sign-off",
  "Onboarding & training included",
  "Export of your data, on request",
  "No third-party advertising or tracking",
];

export default function HousingDomain() {
  return (
    <>
      {/* PAGE HEADER */}
      <section data-testid="housing-page-header" className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Overline>Solutions · Housing, Homelessness &amp; Basic Needs</Overline>
            <StatusChip status="LIVE" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            A compliant HNA. A live evidence base. Named sign-off.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mt-8 max-w-3xl leading-relaxed">
            Housing Needs Assessments are legislated, recurring, and tied to funding — and a
            consultant HNA costs $50–150k for a PDF that's out of date on submission.
            Continuum produces the regulated report from a versioned engine (BC HNR and
            federal HICC), keeps the evidence live between cycles, and leaves your
            community's data foundation in place for everything that comes next.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <PrimaryCTA to="/contact" testid="housing-walkthrough-cta">Book a walkthrough</PrimaryCTA>
            <SecondaryCTA to="/method" testid="housing-method-cta">Read the method</SecondaryCTA>
          </div>
        </div>
      </section>

      {/* THE PROBLEM IN THIS SYSTEM */}
      <Section testid="housing-problem" className="!pb-12">
        <Overline className="mb-6">The problem in this system</Overline>
        <div className="space-y-5 text-slate-700 text-lg md:text-xl leading-relaxed max-w-4xl">
          <p>
            What is core housing need, and where is the affordability gap? How many
            non-market units exist against the deficit? What does the homelessness system
            actually look like — counts, chronic homelessness, shelter capacity, by-name
            lists? And where does housing funding land relative to measured need?
          </p>
          <p>
            The regulatory context makes these questions unavoidable: the federal HICC HNA
            template, the BC HNR method, Alberta GOA guidelines, provincial housing
            strategies, and Housing Target Orders all demand place-based evidence on a
            legislated cycle.
          </p>
        </div>
      </Section>

      {/* THE OBLIGATION, PLAINLY */}
      <Section testid="housing-obligation">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Overline className="mb-6">The obligation, plainly</Overline>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight">
              HNAs are not optional, not one-time, and not separable from funding.
            </h2>
            <div className="mt-6 space-y-5 text-slate-700 text-lg leading-relaxed">
              <p>
                A Housing Needs Assessment is the legislated front door for municipal housing
                policy and senior-government funding flows. The federal template and the
                BC HNR method change on documented cycles. Comparability across cycles is the
                difference between an evidence-based plan and a snapshot.
              </p>
              <p>
                Continuum implements both the federal HNA template and the BC HNR method as
                versioned methods. Outputs carry the version stamp they were produced under,
                so the next refresh produces a comparable report — not a competing one.
              </p>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="bg-[#F8F9FA] border border-slate-200 p-8">
              <Overline className="mb-4">What Continuum produces</Overline>
              <ul className="space-y-4">
                {[
                  "Compliant Housing Needs Report (federal / BC HNR)",
                  "Versioned, lineage-traceable indicator panels",
                  "Named professional sign-off on the statutory output",
                  "Live monitoring dashboard between refresh cycles",
                  "Methodology version notes in the appendix",
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#0A1224]">
                    <CheckCircle2 className="text-[#FF8A00] flex-shrink-0 mt-1" size={18} />
                    <span className="font-medium">{x}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section testid="housing-how-it-works" className="bg-[#0A1224] text-white">
        <Overline className="text-slate-400 mb-6">How it works</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-4xl mb-12">
          Five steps. Analyst-operated MVP. <span className="text-[#FF8A00]">"With our analysts" is a trust feature.</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`housing-step-${i}`} className="bg-[#0A1224] p-8">
              <div className="flex items-center justify-between mb-5">
                <div className="text-[#FF8A00] font-black text-3xl tabular-nums tracking-tighter">{s.n}</div>
                <ArrowRight className="text-white/30" size={18} />
              </div>
              <h3 className="text-xl font-black tracking-tight">{s.t}</h3>
              <p className="text-slate-300 leading-relaxed mt-3">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* THE AMORTISATION ARGUMENT */}
      <Section testid="housing-amortise">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ChoroplethMotif className="w-full border border-slate-200" />
          </div>
          <div className="lg:col-span-6">
            <Overline className="mb-6">The amortisation argument</Overline>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight">
              The HNA builds your community's data foundation. <span className="text-[#FF8A00]">Every other module runs on it.</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mt-6">
              A consulting HNA delivers a beautiful PDF and walks away. A Continuum HNA
              produces the same compliant document — and leaves the evidence layer in place.
              Community intelligence, monitoring, mental-health and seniors' modules, and the
              service-landscape layer all run on what the HNA assembled. The investment
              compounds.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <SecondaryCTA to="/solutions/mental-health" testid="housing-mh-cta">Mental health, next</SecondaryCTA>
              <SecondaryCTA to="/platform#service-landscape" testid="housing-landscape-cta">Service-landscape layer</SecondaryCTA>
            </div>
          </div>
        </div>
      </Section>

      {/* MONITORING & COMPLIANCE — absorbed from old Monitoring page */}
      <Section testid="housing-monitoring" className="bg-[#F8F9FA]">
        <div id="monitoring" className="scroll-mt-24">
          <Overline className="mb-6">Monitoring &amp; compliance</Overline>
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-4xl">
            A plan that doesn't go stale the day it ships.
          </h2>
          <p className="text-slate-700 text-lg leading-relaxed mt-6 max-w-4xl">
            Initiatives are first-class records. For BC municipalities under Housing Target
            Orders, annual sub-target schedules and statutory actions-taken reporting are
            generated from live records — with a public transparency page.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 mt-12">
            {[
              { icon: Activity, t: "Live indicators", d: "Indicator panels refresh on their source's cadence — not on the consulting calendar." },
              { icon: Calendar, t: "Cycle-aligned reports", d: "Compliance dashboards tracking the legislated reporting cycle, with reminders and draft exports." },
              { icon: FileCheck2, t: "Versioned outputs", d: "Method-version bumps generate a new versioned report. Old versions remain inspectable." },
              { icon: RefreshCw, t: "Audit & lineage", d: "Every change to every indicator is logged. Auditors can reproduce a number to its source on any past date." },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={b.t} data-testid={`housing-monitoring-pillar-${i}`} className="bg-white p-8">
                  <Icon className="text-[#FF8A00]" size={28} />
                  <h3 className="text-xl font-black tracking-tight mt-6">{b.t}</h3>
                  <p className="text-slate-600 mt-3 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* MODULES & PRODUCTS */}
      <Section testid="housing-products">
        <Overline className="mb-6">Modules &amp; products</Overline>
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter leading-tight max-w-4xl mb-12">
          Four modules live. <span className="text-[#FF8A00]">Four more building on the same foundation.</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
          {PRODUCTS.map((p, i) => {
            const live = p.status === "LIVE";
            return (
              <div
                key={p.t}
                data-testid={`housing-product-${i}`}
                className={`p-8 flex flex-col ${live ? "bg-white" : "bg-[#F8F9FA]"}`}
              >
                <StatusChip status={p.status} className="self-start" />
                <h3 className={`text-lg font-black tracking-tight mt-5 ${live ? "text-[#0A1224]" : "text-slate-600"}`}>{p.t}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed text-sm flex-1">{p.d}</p>
                {live && (
                  <Link
                    to="/contact"
                    data-testid={`housing-product-${i}-cta`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0A1224] hover:text-[#FF8A00]"
                  >
                    Talk to us <ArrowUpRight size={16} />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* PRICING — absorbed from killed /pricing page */}
      <Section testid="housing-pricing" className="bg-[#F8F9FA]">
        <div id="pricing" className="scroll-mt-24">
          <Overline className="mb-6">Pricing</Overline>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight max-w-3xl">
            Published pricing. <span className="text-[#FF8A00]">On principle.</span>
          </h2>
          <p className="text-slate-600 text-lg leading-relaxed mt-4 max-w-3xl">
            Hiding prices contradicts our manifesto. Consultant HNAs run $50–150k; fair
            pricing is architectural, not charitable — our cost base makes honest pricing a
            design decision.
          </p>
          <div className="mt-10">
            <PricingTable
              rows={PRICING_ROWS}
              footnote="Prices in CAD. Ranges reflect community size and scope; final scope confirmed at walkthrough."
              testid="housing-pricing-table"
            />
          </div>
          <div className="mt-12 bg-white border border-slate-200 p-8">
            <Overline className="mb-2">Always included</Overline>
            <p className="text-[#0A1224] font-bold text-lg mb-6">The principles ship with every tier.</p>
            <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ALWAYS_INCLUDED.map((x, i) => (
                <li key={i} className="flex items-start gap-3 text-[#0A1224]">
                  <CheckCircle2 className="text-[#FF8A00] flex-shrink-0 mt-1" size={18} />
                  <span className="font-medium">{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* FAQ — procurement */}
      <Section testid="housing-faq">
        <Overline className="mb-6">FAQ — procurement</Overline>
        <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-10 max-w-3xl">
          The questions evaluators score against.
        </h2>
        <div className="space-y-px bg-slate-200">
          {[
            { q: "Who signs off on the report?", a: "A named professional — currently Chantal Hansen — whose signature is registered against the output and reachable for follow-up." },
            { q: "Where does our community's data live?", a: "Canadian regions only. Your data layer stays yours, exportable on request, never used to train external models." },
            { q: "What happens at refresh cycles?", a: "Method-version bumps produce a new versioned output. Year-over-year indicators carry their version stamp; comparability is engineered in." },
            { q: "How does Continuum's cost compare with a consulting engagement?", a: "A single one-off consulting HNA typically costs many times Continuum's annual Municipal tier — and ages the day it ships. We make this argument about the consulting model generally; never about a named competitor." },
          ].map((f, i) => (
            <details key={i} data-testid={`housing-faq-${i}`} className="bg-white p-6 group">
              <summary className="cursor-pointer font-bold text-lg text-[#0A1224] flex items-center justify-between list-none">
                {f.q}
                <span className="text-[#FF8A00] text-2xl font-black transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </Section>

      <Section testid="housing-trust" className="!py-16"><TrustStrip /></Section>

      {/* FINAL CTA */}
      <Section testid="housing-cta" className="bg-[#0A1224] text-white text-center">
        <h2 className="text-3xl md:text-5xl font-black tracking-tighter max-w-3xl mx-auto leading-tight">
          Walkthrough is with the person who will sign your report.
        </h2>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <PrimaryCTA to="/contact" testid="housing-final-cta">Book a walkthrough</PrimaryCTA>
        </div>
      </Section>
    </>
  );
}
